// src/screens/VehicleExit.js v1.4
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getActiveVehicleByPlate, getActiveVehicles, logVehicleExit, calculateFee, calculateOvernightFee, getSettings, getMouById, getMouReceivables, addTip, buildWhatsAppMessage } from '../database/db';
import { format } from 'date-fns';
import { Printer, DollarSign, CreditCard, Mic, ArrowLeft, Search } from 'lucide-react';
import { getPrinterDevice, printExitReceipt } from '../utils/bluetooth-printer';
import QRCode from 'qrcode';

const fmt = n => `AED ${Number(n||0).toFixed(2)}`;
const fmtTime = iso => iso ? format(new Date(iso),'hh:mm a') : '—';
const fmtDate = iso => iso ? format(new Date(iso),'dd/MM/yyyy') : '—';
const getLabel = pt => ({'monthly-24hr':'Monthly 24hr','monthly-night':'Monthly Night','monthly-day':'Monthly Day','weekly':'Weekly','biweekly':'Bi-Weekly','hourly':'Hourly','overnight':'Overnight','mou':'MOU'}[pt]||pt||'Hourly');

export default function VehicleExit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [autoLoaded, setAutoLoaded] = React.useState(false);
  const [plateSearch, setPlateSearch] = useState('');
  const [step, setStep] = useState('list');
  const [vehicle, setVehicle] = useState(null);
  const [feeData, setFeeData] = useState(null);
  const [overnightFeeData, setOvernightFeeData] = useState(null);
  const [payMethod, setPayMethod] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [tip, setTip] = useState('');
  const [alert, setAlert] = useState(null);
  const [exitRecord, setExitRecord] = useState(null);
  const [qrData, setQrData] = useState('');
  // Change #2 — auto-loaded on mount
  const [activeList, setActiveList] = useState([]);
  const [listening, setListening] = useState(false);
  const [receiptPhone, setReceiptPhone] = useState('');
  const [btPrinting, setBtPrinting] = useState(false);
  const [btAlert, setBtAlert] = useState('');
  const settings = getSettings();

  // Change #2 — auto-load parked vehicles on mount
  // Quick exit from dashboard - auto-select plate if passed via state
  useEffect(() => {
    if (location.state?.plate && !autoLoaded) {
      setAutoLoaded(true);
      setPlateSearch(location.state.plate);
      const v = getActiveVehicles().find(x => x.plate === location.state.plate);
      if (v) selectVehicle(v);
    }
  }, [location.state, autoLoaded]);
  useEffect(() => { setActiveList(getActiveVehicles()); }, []);

  const filteredList = plateSearch
    ? activeList.filter(v => v.plate.includes(plateSearch.toUpperCase()))
    : activeList;

  const handleVoice = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) { setAlert({type:'warning',msg:'Voice not supported. Type manually.'}); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.lang='en-AE';
    r.onstart=()=>setListening(true);
    r.onresult=e=>{setPlateSearch(e.results[0][0].transcript.toUpperCase().replace(/\s/g,''));setListening(false);};
    r.onerror=()=>setListening(false); r.onend=()=>setListening(false);
    r.start();
  };

  const selectVehicle = (v) => {
    setVehicle(v); setAlert(null); setReceiptPhone(v.customerPhone||'');
    const now = new Date().toISOString();
    if (v.packageType==='overnight'&&v.overnightRate) {
      setOvernightFeeData(calculateOvernightFee(v.entryTime,now,v.overnightRate,v.overnightEndTime||'07:00',settings.hourlyRate));
      setFeeData(null);
    } else if (!v.tenantId) {
      const rate = v.isMou?(getMouById(v.mouId)?.ratePerHour||5):(v.hourlyRate||settings.hourlyRate);
      setFeeData(calculateFee(v.entryTime,now,rate,0)); setOvernightFeeData(null);
    }
    setStep('confirm');
  };

  const handleExit = async () => {
    if (!payMethod&&!vehicle.tenantId&&!(overnightFeeData&&overnightFeeData.extraFee===0)) { setAlert({type:'danger',msg:'Select payment method.'}); return; }
    const now = new Date();
    const discAmt = parseFloat(discount)||0;
    let finalFee=0,finalFeeData=feeData,finalOvData=overnightFeeData;

    if (vehicle.packageType==='overnight'&&vehicle.overnightRate) {
      finalOvData = calculateOvernightFee(vehicle.entryTime,now.toISOString(),vehicle.overnightRate,vehicle.overnightEndTime||'07:00',settings.hourlyRate);
      finalFee = Math.max(0, finalOvData.extraFee - discAmt); // Only extra beyond advance
    } else if (feeData) {
      finalFeeData = calculateFee(vehicle.entryTime,now.toISOString(),vehicle.isMou?(getMouById(vehicle.mouId)?.ratePerHour||5):(vehicle.hourlyRate||settings.hourlyRate),discAmt);
      finalFee = finalFeeData.finalFee;
    }

    const exitData = {
      exitTime:now.toISOString(),
      fee:finalFee,
      grossFee:finalFeeData?.grossFee||finalOvData?.totalFee||0,
      discountAmount:discAmt,discountReason:discAmt>0?discountReason:'',
      paymentMethod:vehicle.tenantId?'advance':(vehicle.isMou?'mou':payMethod),
      exitStaffId:user.id,exitStaffName:user.name,
      duration:finalFeeData?.breakdown||(finalOvData?`${vehicle.overnightEndTime} +${finalOvData.extraMins}min`:'—'),
    };

    const exited = await logVehicleExit(vehicle.id, exitData);

    if (vehicle.isMou) {
      const list = await getMouReceivables().map(r=>r.vehicleId===vehicle.id?{...r,fee:finalFee,exitTime:now.toISOString()}:r);
      localStorage.setItem('ps_mou_receivables',JSON.stringify(list));
    }
    if (tip&&parseFloat(tip)>0) await addTip({amount:parseFloat(tip),plate:vehicle.plate,vehicleId:vehicle.id,staffId:user.id,staffName:user.name,date:now.toISOString().split('T')[0]});

    const qr = await QRCode.toDataURL(JSON.stringify({plate:vehicle.plate,entry:vehicle.entryTime,exit:now.toISOString(),fee:finalFee,method:exitData.paymentMethod,staff:user.name})).catch(()=>'');
    setQrData(qr);
    setExitRecord({...exited,...exitData,feeData:finalFeeData,overnightFeeData:finalOvData});
    setStep('receipt');
  };

  // Change #1 — WhatsApp always available
  const sendWhatsApp = () => {
    const msg = buildWhatsAppMessage('exit',{
      date:fmtDate(exitRecord.exitTime),entryTime:fmtTime(exitRecord.entryTime),
      exitTime:fmtTime(exitRecord.exitTime),duration:exitRecord.duration,
      plate:exitRecord.plate,packageType:getLabel(exitRecord.packageType),
      advance:exitRecord.overnightAdvance>0?exitRecord.overnightAdvance.toFixed(2):null,
      extra:exitRecord.overnightFeeData?.extraFee>0?exitRecord.overnightFeeData.extraFee.toFixed(2):null,
      discount:exitRecord.discountAmount>0?exitRecord.discountAmount.toFixed(2):null,
      fee:exitRecord.fee?.toFixed(2)||'0.00',
      balance:exitRecord.overnightFeeData&&exitRecord.overnightFeeData.extraFee>0?exitRecord.fee?.toFixed(2):null,
      method:exitRecord.paymentMethod?.toUpperCase(),staff:exitRecord.exitStaffName
    });
    const num = receiptPhone.replace(/\D/g,'');
    const wa = num.startsWith('0')?'971'+num.slice(1):num.startsWith('971')?num:'971'+num;
    window.open(`https://wa.me/${wa}?text=${msg}`,'_blank');
  };

  const btPrint = async() => {
    const device = getPrinterDevice();
    if(!device){setBtAlert('Printer not connected. Go to Settings → Printer.');setTimeout(()=>setBtAlert(''),4000);return;}
    setBtPrinting(true);
    try{
      const {autoReconnectAndPrint} = await import('../utils/bluetooth-printer');
      await autoReconnectAndPrint('exit',{...exitRecord,overnightFeeData},settings);setBtAlert('✅ Printed!');}
    catch(e){setBtAlert('❌ '+e.message);}
    setBtPrinting(false);
    setTimeout(()=>setBtAlert(''),4000);
  };

  const reset = () => {
    setPlateSearch(''); setStep('list'); setVehicle(null); setFeeData(null); setOvernightFeeData(null);
    setPayMethod(''); setDiscount(''); setDiscountReason(''); setTip('');
    setAlert(null); setExitRecord(null); setQrData(''); setReceiptPhone('');
    setActiveList(getActiveVehicles());
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">🏁 Vehicle Exit</div><div className="page-subtitle">Process exit and collect payment</div></div>
        {step!=='list'&&<button className="btn btn-outline btn-sm" onClick={reset}>← Back</button>}
      </div>
      <div className="page-body">
        {alert&&<div className={`alert alert-${alert.type}`}>{alert.msg}</div>}

        {/* Change #2 — LIST VIEW with search at top, auto-loaded */}
        {step==='list'&&(
          <div>
            {/* Search bar always visible at top */}
            <div className="card" style={{marginBottom:10}}>
              <div className="search-bar" style={{marginBottom:8}}>
                <Search size={14} className="search-icon"/>
                <input
                  className="form-input plate-input"
                  style={{paddingLeft:36,paddingRight:48,fontSize:18}}
                  placeholder="SEARCH PLATE..."
                  value={plateSearch}
                  onChange={e=>setPlateSearch(e.target.value.toUpperCase())}
                  autoFocus
                />
                <button
                  onClick={handleVoice}
                  style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:listening?'var(--danger)':'var(--text-muted)',padding:4}}
                >
                  <Mic size={16}/>
                </button>
              </div>
              {plateSearch&&filteredList.length===0&&(
                <div className="alert alert-danger" style={{margin:0}}>⚠️ Plate <strong>{plateSearch}</strong> not found in parked vehicles.</div>
              )}
            </div>

            {activeList.length===0
              ?<div className="card"><div className="empty-state"><div className="empty-state-icon">🅿️</div><div className="empty-state-text">No vehicles currently parked</div></div></div>
              :<div className="card">
                <div className="card-title">Parked Vehicles ({filteredList.length}{plateSearch?` of ${activeList.length}`:''})</div>
                {filteredList.map(v=>(
                  <div key={v.id} className="vehicle-card" onClick={()=>selectVehicle(v)}>
                    <div className="vehicle-plate">{v.plate}</div>
                    <div className="vehicle-info">
                      <div className="vehicle-type">{getLabel(v.packageType)} {v.isMou&&<span className="badge badge-mou" style={{fontSize:9}}>MOU</span>}</div>
                      <div className="vehicle-time">In: {fmtTime(v.entryTime)}</div>
                    </div>
                    <div style={{fontSize:12,color:v.carType==='open'?'var(--success)':'var(--warning)',flexShrink:0}}>{v.carType==='open'?'◯':'●'}</div>
                  </div>
                ))}
              </div>
            }
          </div>
        )}

        {/* CONFIRM & PAY */}
        {step==='confirm'&&vehicle&&(
          <div style={{maxWidth:500,margin:'0 auto'}}>
            <div className="card">
              <div style={{textAlign:'center',marginBottom:14}}>
                <div style={{fontFamily:'var(--font-mono)',fontSize:22,fontWeight:700,color:'var(--accent)',letterSpacing:'0.1em'}}>{vehicle.plate}</div>
                <div style={{marginTop:6,display:'flex',gap:6,justifyContent:'center',flexWrap:'wrap'}}>
                  {vehicle.isMou&&<span className="badge badge-mou">MOU</span>}
                  {vehicle.tenantId&&<span className="badge badge-success">PACKAGE</span>}
                  {vehicle.packageType==='overnight'&&<span className="badge badge-blue">🌙 OVERNIGHT</span>}
                </div>
              </div>

              <div style={{background:'var(--bg-input)',borderRadius:'var(--radius-sm)',padding:12,marginBottom:14}}>
                {[['Entry',fmtTime(vehicle.entryTime)],['Exit',fmtTime(new Date().toISOString())],['Package',getLabel(vehicle.packageType)]].map(([k,v])=>(
                  <div key={k} className="flex-between" style={{marginBottom:6}}>
                    <span style={{fontSize:12,color:'var(--text-muted)'}}>{k}</span>
                    <span style={{fontSize:13,fontWeight:600}}>{v}</span>
                  </div>
                ))}

                {/* Change #9 — Overnight advance + balance display */}
                {overnightFeeData&&(<>
                  <div className="divider"/>
                  <div className="flex-between" style={{marginBottom:4}}><span style={{fontSize:12,color:'var(--text-muted)'}}>Agreed Overnight</span><span style={{fontFamily:'var(--font-mono)'}}>{fmt(overnightFeeData.agreedRate)}</span></div>
                  {vehicle.overnightAdvance>0&&<div className="flex-between" style={{marginBottom:4}}><span style={{fontSize:12,color:'var(--success)'}}>Advance Paid</span><span style={{fontFamily:'var(--font-mono)',color:'var(--success)'}}>- {fmt(vehicle.overnightAdvance)}</span></div>}
                  <div className="flex-between" style={{marginBottom:4}}><span style={{fontSize:12,color:'var(--text-muted)'}}>Agreed Until</span><span>{overnightFeeData.agreedEndTime}</span></div>
                  {overnightFeeData.extraFee>0&&<div className="flex-between" style={{marginBottom:4}}><span style={{fontSize:12,color:'var(--danger)'}}>Extra Hours ({overnightFeeData.extraMins}min)</span><span style={{fontFamily:'var(--font-mono)',color:'var(--danger)'}}>+ {fmt(overnightFeeData.extraFee)}</span></div>}
                  <div className="divider"/>
                  <div className="flex-between">
                    <span style={{fontSize:15,fontWeight:700}}>{overnightFeeData.extraFee>0?'Balance Due':'Nothing Due'}</span>
                    <span style={{fontSize:22,fontFamily:'var(--font-display)',fontWeight:800,color:overnightFeeData.extraFee>0?'var(--danger)':'var(--success)'}}>
                      {overnightFeeData.extraFee>0?fmt(overnightFeeData.extraFee):'AED 0.00'}
                    </span>
                  </div>
                  {overnightFeeData.extraFee===0&&<div style={{fontSize:12,color:'var(--success)',textAlign:'center',marginTop:4}}>✅ Fully paid in advance</div>}
                </>)}

                {feeData&&(<>
                  <div className="divider"/>
                  <div className="flex-between" style={{marginBottom:4}}><span style={{fontSize:12,color:'var(--text-muted)'}}>Duration</span><span>{feeData.breakdown}</span></div>
                  <div className="flex-between" style={{marginBottom:4}}><span style={{fontSize:12,color:'var(--text-muted)'}}>Gross Fee</span><span style={{fontFamily:'var(--font-mono)'}}>{fmt(feeData.grossFee)}</span></div>
                  {feeData.discount>0&&<div className="flex-between" style={{marginBottom:4}}><span style={{fontSize:12,color:'var(--danger)'}}>Discount</span><span style={{color:'var(--danger)'}}>- {fmt(feeData.discount)}</span></div>}
                  <div className="flex-between" style={{marginTop:8}}>
                    <span style={{fontSize:15,fontWeight:700}}>Total Due</span>
                    <span style={{fontSize:22,fontFamily:'var(--font-display)',fontWeight:800,color:'var(--accent)'}}>{fmt(feeData.finalFee)}</span>
                  </div>
                </>)}

                {vehicle.tenantId&&<div className="alert alert-success" style={{margin:'8px 0 0'}}>Package vehicle — no payment at exit.</div>}
                {vehicle.isMou&&<div className="alert alert-info" style={{margin:'8px 0 0'}}>MOU — billed to {vehicle.mouCompanyName}.</div>}
              </div>

              {!vehicle.tenantId&&(overnightFeeData?.extraFee>0||feeData)&&(
                <div className="form-group">
                  <label className="form-label">Discount (AED) — optional</label>
                  <input className="form-input" type="number" placeholder="0.00" value={discount} onChange={e=>setDiscount(e.target.value)}/>
                  {discount>0&&<input className="form-input" style={{marginTop:6}} placeholder="Reason for discount" value={discountReason} onChange={e=>setDiscountReason(e.target.value)}/>}
                </div>
              )}

              {!vehicle.tenantId&&!vehicle.isMou&&(overnightFeeData?.extraFee>0||(!overnightFeeData))&&(
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <div className="payment-select">
                    <button className={`payment-btn ${payMethod==='cash'?'selected-cash':''}`} onClick={()=>setPayMethod('cash')}><DollarSign size={22}/>CASH</button>
                    <button className={`payment-btn ${payMethod==='card'?'selected-card':''}`} onClick={()=>setPayMethod('card')}><CreditCard size={22}/>CARD</button>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Tip Received (AED) — optional</label>
                <input className="form-input" type="number" placeholder="0.00" value={tip} onChange={e=>setTip(e.target.value)}/>
              </div>

              <button className="btn btn-success btn-full btn-lg" onClick={handleExit}>
                ✅ {overnightFeeData&&overnightFeeData.extraFee===0?'Release (Fully Paid)':vehicle.tenantId?'Release':'Confirm Exit & Collect'}
              </button>
            </div>
          </div>
        )}

        {/* RECEIPT */}
        {step==='receipt'&&exitRecord&&(
          <div style={{maxWidth:420,margin:'0 auto'}}>
            <div className="alert alert-success">✅ Exit processed.</div>
            <div className="receipt">
              <div className="receipt-header">
                <div className="receipt-title">AL BARAKAH CAR PARKING</div>
                <div style={{fontSize:10,color:'#555'}}>Arabian Gulf Street, Sharjah</div>
                <div style={{fontSize:10,fontWeight:700,marginTop:2}}>— EXIT RECEIPT —</div>
              </div>
              <hr className="receipt-divider"/>
              <div className="receipt-row"><span>Date:</span><span>{fmtDate(exitRecord.exitTime)}</span></div>
              <div className="receipt-row"><span>Entry:</span><span>{fmtTime(exitRecord.entryTime)}</span></div>
              <div className="receipt-row"><span>Exit:</span><span>{fmtTime(exitRecord.exitTime)}</span></div>
              <div className="receipt-row"><span>Duration:</span><span>{exitRecord.duration}</span></div>
              <div className="receipt-row"><span>Plate:</span><span style={{fontWeight:700}}>{exitRecord.plate}</span></div>
              <div className="receipt-row"><span>Package:</span><span>{getLabel(exitRecord.packageType)}</span></div>

              {/* Change #9 — Overnight advance on exit receipt */}
              {exitRecord.overnightFeeData&&(<>
                <hr className="receipt-divider"/>
                <div className="receipt-row"><span>Agreed Rate:</span><span>AED {exitRecord.overnightFeeData.agreedRate}</span></div>
                {vehicle?.overnightAdvance>0&&<div className="receipt-row"><span>Advance Paid:</span><span style={{color:'#060',fontWeight:700}}>AED {vehicle.overnightAdvance.toFixed(2)}</span></div>}
                {exitRecord.overnightFeeData.extraFee>0&&<div className="receipt-row"><span>Extra Hours:</span><span style={{color:'#c00'}}>AED {exitRecord.overnightFeeData.extraFee.toFixed(2)}</span></div>}
              </>)}

              <hr className="receipt-divider"/>
              {exitRecord.discountAmount>0&&<div className="receipt-row"><span>Discount:</span><span>- AED {exitRecord.discountAmount.toFixed(2)}</span></div>}
              {exitRecord.overnightFeeData&&exitRecord.overnightFeeData.extraFee===0
                ?<div className="receipt-row receipt-total"><span>BALANCE DUE:</span><span style={{color:'#060'}}>AED 0.00 ✅</span></div>
                :<div className="receipt-row receipt-total"><span>TOTAL:</span><span>AED {exitRecord.fee?.toFixed(2)||'0.00'}</span></div>
              }
              {exitRecord.overnightFeeData&&exitRecord.overnightFeeData.extraFee>0&&(
                <div className="receipt-row receipt-balance"><span>BALANCE DUE:</span><span>AED {exitRecord.fee?.toFixed(2)||'0.00'}</span></div>
              )}
              <div className="receipt-row"><span>Payment:</span><span style={{textTransform:'uppercase'}}>{exitRecord.paymentMethod}</span></div>
              <div className="receipt-row"><span>Staff:</span><span>{exitRecord.exitStaffName}</span></div>
              {qrData&&<div style={{textAlign:'center',marginTop:10}}><img src={qrData} alt="QR" style={{width:60,height:60}}/></div>}
              <hr className="receipt-divider"/>
              <div style={{textAlign:'center',fontSize:9,color:'#666'}}>{settings.receiptFooter}</div>
            </div>

            {/* Change #1 — WhatsApp always visible on exit receipt */}
            <div style={{marginTop:12,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'12px'}}>
              <label className="form-label">📱 Send via WhatsApp</label>
              <div className="flex-row">
                <input className="form-input" type="tel" placeholder="+971 50 000 0000" value={receiptPhone} onChange={e=>setReceiptPhone(e.target.value)} style={{flex:1}}/>
                <button className="btn btn-whatsapp" onClick={receiptPhone?sendWhatsApp:()=>setAlert({type:'danger',msg:'Enter phone number'})} disabled={!receiptPhone}>💬 Send</button>
              </div>
            </div>

            {btAlert&&<div className={`alert ${btAlert.startsWith('✅')?'alert-success':'alert-danger'}`} style={{marginTop:8}}>{btAlert}</div>}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:10}}>
              <button className="btn btn-ghost btn-full" onClick={()=>navigate('/dashboard')}><ArrowLeft size={13}/> Dashboard</button>
              <button className="btn btn-success btn-full" onClick={btPrint} disabled={btPrinting}>{btPrinting?'Printing...':'🖨️ BT Print'}</button>
              <button className="btn btn-outline btn-full" onClick={()=>window.print()}>🖨️ Browser</button>
              <button className="btn btn-primary btn-full" onClick={reset}>+ New Exit</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
